import { Component, OnChanges, SimpleChanges, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';

import { WatchlistUtils } from '../../../../../services/watchlist/watchlist-utils';
import { DetailedQuote } from '../../../../../domain/quote.domain';
import { SecuritySymbol, SymbolType } from '../../../../../domain/symbol.domain';
import {
  TickerSymbolWarningsComponent
} from '../../../../ticker-symbol/ticker-symbol-warning/ticker-symbol-warnings.component';
import { TickerSymbolStatus } from '../../../../ticker-symbol/ticker-symbol.domain';
import { AddToWatchlistComponent } from '../../add-to-watchlist/add-to-watchlist.component';
import { SlideAddToWatchlistComponent } from '../../add-to-watchlist/slide-add-to-watchlist.component';
import { AddToWatchlistToastMessageDirective } from '../../add-to-watchlist/add-to-watchlist-toast-message.directive';
import { InterlistedExchange, QuoteIssueType } from '../../domain';
import { TodaysChangeComponent } from '../../todays-change/todays-change.component';
import { SplitButtonsComponent } from '../../../../../components/split-buttons/split-buttons.component';
import { SplitButtonComponent } from '../../../../../components/split-buttons/split-button.component';
import { SplitButtonDataComponent } from '../../../../../components/split-buttons/split-button-data.component';
import { PlaceholderPipe } from '../../../../../pipes/placeholder.pipe';
import { IconFlagComponent } from '../../../../../components/icon/icon-flag.component';
import { CurrencyPipe } from '../../../../../pipes/currency.pipe';
import { SecurityService } from '../../../../../services/security.service';
import { TranslatePipe } from '../../../../../pipes/translate.pipe';

export type SymbolWithExchanges =
  Pick<DetailedQuote, 'market' | 'symbol' | 'name' | 'issueType' | 'interlistedExchanges' | 'lastPrice' | 'securityHalted' | 'isHalted'>;

@Component({
  selector: 'td-wb-detailed-symbol-header',
  templateUrl: 'detailed-symbol-header.component.html',
  styleUrls: ['detailed-symbol-header.component.scss'],
  imports: [
    SplitButtonsComponent,
    SplitButtonComponent,
    SplitButtonDataComponent,
    FormsModule,
    PlaceholderPipe,
    IconFlagComponent,
    TranslatePipe,
    TranslateDirective,
    TickerSymbolWarningsComponent,
    CurrencyPipe,
    TodaysChangeComponent,
    AddToWatchlistComponent,
    SlideAddToWatchlistComponent,
    AddToWatchlistToastMessageDirective,
    TranslatePipe
  ]
})
export class DetailedSymbolHeaderComponent implements OnChanges  {
  private readonly securityService = inject(SecurityService);

  readonly viewSizeCompact = input<boolean>(false);
  readonly quote = input<SymbolWithExchanges>(undefined);
  readonly displaySymbolDetail = input<boolean>(undefined);
  readonly symbol = input<SecuritySymbol>(undefined);
  readonly hideAddToWatchlistButton = input<boolean>(undefined);
  readonly hideInterlistExchangeForOTC = input<boolean>(false);
  readonly useSlider = input<boolean>(false); // REFACTOR. FOR ET ONLY
  readonly onShowExchange = output<void>();

  selectedMarket: string;
  markets: Array<string> = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.quote !== undefined &&
        changes.quote.currentValue !== undefined  &&
        this.hasInterlistedExchanges()) {
      this.selectedMarket = this.quote().market;
      if (this.markets.length !== 2) {
        this.markets[0] = this.quote().market;
        this.markets[1] = this.quote().interlistedExchanges[0].exchange;
      }
    }
  }

  /**
   * Return true if quote has inter listed exchanges
   *
   * @return
   */
  hasInterlistedExchanges(): boolean {
    const quote = this.quote();
    return quote?.interlistedExchanges !== undefined &&
           quote?.interlistedExchanges?.length > 0;
  }

  /**
   * Return true if quote issue type is 'STOCK' or 'ETF'
   *
   * @return
   */
  isStockEtf(): boolean {
    const quote = this.quote();
    return quote?.issueType === QuoteIssueType.STOCK || quote?.issueType === QuoteIssueType.ETF;
  }

  isETF(): boolean {
    return this.symbol().subTypeCd === SymbolType.ETF;
  }

  /**
   * Return true if quote issue type is 'INDEX'
   *
   * @return
   */
  isIndex(): boolean {
    return this.quote().issueType === QuoteIssueType.INDEX;
  }

  /**
   * Return true if quote issue type is 'OPTION'
   *
   * @return
   */
  isOption(): boolean {
    return this.quote().issueType === QuoteIssueType.OPTION;
  }

  /**
   * Emits event to show interlisted exchange quotes
   */
  onShowInterlistedExchangeQuote(): void {
    // TODO: The 'emit' function requires a mandatory void argument
    this.onShowExchange.emit();
  }

  getSymbolStatus(): TickerSymbolStatus {
    const quoteValue = this.quote();
    if (quoteValue !== undefined) {
      const quote = { ...quoteValue };
      quote.securityHalted = this.symbolDetailSecurityHalted();
      return WatchlistUtils.toSymbolStatus(quote, this.symbol());
    }
  }

  showInterlistedExchanges(): boolean {
    return this.isStockEtf() &&
           this.hasInterlistedExchanges() &&
           !this.symbolDetailSecurityHalted() && !this.hideInterlistedTab();
  }

  showAddToWatchlist(): boolean {
    return !this.hideAddToWatchlistButton() &&
           this.hasWatchListPermission();
  }

  private symbolDetailSecurityHalted(): boolean {
    return this.displaySymbolDetail() && this.quote()?.isHalted;
  }

  private hideInterlistedTab(): boolean {
    const quote = this.quote();
    return this.hideInterlistExchangeForOTC() &&
      (quote?.interlistedExchanges.find((exchange: InterlistedExchange): boolean => exchange.exchange.includes('OTC')) !== undefined ||
      quote?.market.includes('OTC'));
  }

  private hasWatchListPermission(): boolean {
    return this.securityService.hasPermission('WATCHLIST');
  }
}
